import React, { useEffect, useState } from 'react';
import { NavigationActions, StackActions } from '@react-navigation/native';
import { View, ScrollView } from 'react-native';
import { shape } from 'prop-types';
import { ModalHeader } from 'LocalGluons';
import { t } from 'Locale';
import SuccessPage from 'Pages/SuccessPage/SuccessPage';
import { RESET_TYPE } from 'Pages/VerificationCodePages/GetHelp/GetHelp';
import { useStore } from 'Store';
import {
    SendCodePassword as SendCode,
    VerifyCodePassword as VerifyCode,
    GetHelp,
    CodeSent
} from '../VerificationCodePages';

import {
    NewPassword,
    NewUsername,
    ChangeCredentials,
    ChangePassword,
    ChangeUsernameQuestion
} from './Pages';

import defaultStyles from './PasswordManagement.jss';
import { useNavigationPropsContext } from '../../Navigation/navigationPropsContext';

export const pages = Object.freeze({
    sendCode: 'sendCode',
    verifyCode: 'verifyCode',
    getHelp: 'getHelp',
    codeSent: 'codeSent',
    newPassword: 'newPassword',
    changePassword: 'changePassword',
    newUsername: 'newUsername',
    changeUsername: 'changeUsername',
    passwordSuccess: 'passwordSuccess',
    usernameSuccess: 'usernameSuccess',
    changeCredentials: 'changeCredentials',
    changeUsernameQuestion: 'changeUsernameQuestion',
    usernameSuccessNoContinue: 'usernameSuccessNoContinue'
});

const PasswordManagement = ({ navigation, route }) => {
    const { navigationProps } = useNavigationPropsContext();

    const initialRoute = route?.params?.initialRoute || undefined;
    const backRoute = route.params?.backRoute || undefined;
    const [page, setPage] = useState(initialRoute);
    const [data, setData] = useState({});
    const { setHideLoginHeader } = useStore();

    useEffect(() => {
        setPage(initialRoute);
    }, [initialRoute]);

    const setPageFactory = (newPage) => (newData) => {
        setPage(newPage);
        if (newData) setData(newData);
    };

    const close = () => {
        if (page === pages.getHelp) {
            setPageFactory(pages.verifyCode)();
        } else if (backRoute) {
            const resetAction = StackActions.reset({
                index: 0,
                actions: [NavigationActions.navigate(backRoute, { routeName: backRoute })]
            });
            navigation.dispatch(resetAction);
        } else {
            navigation.pop();
            setHideLoginHeader(false);
        }
    };

    const pageComponents = {
        [pages.changeCredentials]: (
            <ChangeCredentials onChange={setPageFactory(pages.newUsername)} onCancel={close} />
        ),
        [pages.sendCode]: <SendCode onSubmitted={setPageFactory(pages.codeSent)} close={close} />,
        [pages.codeSent]: (
            <CodeSent
                title={t('COMMON.IFORGOT_PASSWORD')}
                onOk={setPageFactory(pages.verifyCode)}
            />
        ),
        [pages.verifyCode]: (
            <VerifyCode
                data={data}
                onSubmitted={setPageFactory(pages.newPassword)}
                onGetHelp={setPageFactory(pages.getHelp)}
                navigation={navigation}
            />
        ),
        [pages.getHelp]: (
            <GetHelp
                data={data}
                onSendAgain={setPageFactory(pages.verifyCode)}
                type={RESET_TYPE.PASSWORD}
            />
        ),
        [pages.newPassword]: (
            <NewPassword
                data={data}
                onSaved={setPageFactory(pages.passwordSuccess)}
                navigation={navigation}
            />
        ),
        [pages.changePassword]: (
            <ChangePassword onCancel={close} onSaved={setPageFactory(pages.passwordSuccess)} />
        ),
        [pages.changeUsernameQuestion]: (
            <ChangeUsernameQuestion
                onSubmitted={setPageFactory(pages.newUsername)}
                onCancel={close}
            />
        ),
        [pages.newUsername]: (
            <NewUsername onSaved={setPageFactory(pages.usernameSuccess)} onCancel={close} />
        ),
        [pages.changeUsername]: (
            <NewUsername
                onSaved={setPageFactory(pages.usernameSuccessNoContinue)}
                onCancel={close}
            />
        ),
        [pages.usernameSuccessNoContinue]: (
            <SuccessPage
                message={t('SETTINGS.SAVE_USERNAME_CONFIRM_TXT')}
                buttonTitle={t('COMMON.OK')}
                onContinue={close}
            />
        ),
        [pages.passwordSuccess]: (
            <SuccessPage message={t('COMMON.PASSWORD_SET_SUCCESS')} onContinue={close} />
        ),
        [pages.usernameSuccess]: (
            <SuccessPage
                message={t('SETTINGS.SAVE_USERNAME_CONFIRM_TXT')}
                buttonTitle={t('COMMON.CONTINUE')}
                onContinue={setPageFactory(pages.changePassword)}
            />
        )
    };

    return (
        <View style={defaultStyles.container}>
            {!pages.sendCode && <ModalHeader onPress={close} />}
            <View style={{ flex: 1 }}>
                <ScrollView contentContainerStyle={defaultStyles.scrollViewContainer}>
                    {pageComponents[page]}
                </ScrollView>
            </View>
        </View>
    );
};

PasswordManagement.propTypes = {
    navigation: shape({}).isRequired
};

export default PasswordManagement;
